import { TTimeline, TTimelineMedEventItem } from './timeline.type'

type TGetFollowupTimelineParams = {
  timeline: TTimeline
  patientFollowupId: number
  showPastMedEvents: boolean
}

type TFollowupTimeline = {
  items: TTimeline
  hasHiddenPastMedEvents: boolean
}

export const getFollowupTimeline = ({
  timeline,
  patientFollowupId,
  showPastMedEvents,
}: TGetFollowupTimelineParams): TFollowupTimeline => {
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)

  const isPast = (medEvent: TTimelineMedEventItem) => new Date(medEvent.date) < startOfToday

  const followupMedEvents = timeline.filter(
    (item): item is TTimelineMedEventItem =>
      item.type === 'MedEvent' && item.PatientFollowupId === patientFollowupId,
  )
  const upcomingMedEvents = followupMedEvents.filter((medEvent) => !isPast(medEvent))
  const lastPastMedEvent = followupMedEvents
    .filter(isPast)
    .sort((medEventA, medEventB) => new Date(medEventB.date).getTime() - new Date(medEventA.date).getTime())
    .at(0)

  let visibleMedEvents = followupMedEvents
  if (!showPastMedEvents) {
    visibleMedEvents =
      upcomingMedEvents.length === 0 && lastPastMedEvent ? [lastPastMedEvent] : upcomingMedEvents
  }

  const visibleMedEventIds = new Set(visibleMedEvents.map((medEvent) => medEvent.PatientMedEventId))
  const followupMedEventIds = new Set(followupMedEvents.map((medEvent) => medEvent.PatientMedEventId))

  if (followupMedEvents.length === 0) {
    // The appointments of this followup may have been deleted: the workflows already executed remain
    return {
      items: timeline.filter(
        (item) => item.type === 'Workflow' && item.done && item.PatientFollowupId === patientFollowupId,
      ),
      hasHiddenPastMedEvents: false,
    }
  }

  return {
    items: timeline.filter((item) => {
      if (item.PatientMedEventId === null) return false
      if (visibleMedEventIds.has(item.PatientMedEventId)) return true
      return item.type === 'Workflow' && !item.done && followupMedEventIds.has(item.PatientMedEventId)
    }),
    hasHiddenPastMedEvents: visibleMedEvents.length < followupMedEvents.length,
  }
}
